/**
 * @file OurContractNumberField.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-08
 * @modified 2026-09-08
 * @description "Our Contract No." — read-only, allocated on request.
 *
 *              The number identifies an agreement between Excel Agri and a
 *              customer, and it used to be free text on both contract modals.
 *              Two had been typed by hand and nothing stopped a third being
 *              EXA2, exa-003, or a duplicate of the first.
 *
 *              Giving a contract a number stays optional — most do not need one
 *              — so this does not allocate on open. The user asks, one sequence
 *              shared by every contract pricing type answers, and the field
 *              shows what will be saved rather than accepting whatever is typed.
 *
 *              Shared by StraightsContractModal and ContractPricingModal: the
 *              same field with two implementations is how the two formats drift
 *              apart again.
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Input from './Input';
import './OurContractNumberField.css';

function OurContractNumberField({ value, onChange, disabled = false, label = 'Our Contract No.' }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const has = !!String(value || '').trim();

  const assign = async () => {
    setBusy(true);
    setError('');
    try {
      // POST because it consumes a number: what comes back is the number this
      // contract will carry, not a preview another user could take first.
      const res = await fetch(`${API_URL}/api/pricing/contract-number/next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not get a contract number');
      onChange(data.contract_number);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="form-group our-contract-number">
      <label>{label}</label>
      <div className="our-contract-number-row">
        <Input
          type="text"
          value={value || ''}
          onChange={() => {}}
          readOnly
          placeholder={has ? '' : 'Not assigned'}
          title="Assigned in sequence — not typed"
        />
        {/* One slot, two states. Disabled rather than hidden in view mode so
            the field keeps its shape between viewing and editing. */}
        <button
          type="button"
          className={`btn-icon${disabled || busy ? ' btn-disabled' : ''}`}
          disabled={disabled || busy}
          title={disabled
            ? 'Contract numbers cannot be changed here'
            : has ? 'Remove this contract number' : 'Assign the next contract number'}
          onClick={has ? () => onChange('') : assign}
        >
          <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>
            {busy ? 'progress_activity' : has ? 'close' : 'tag'}
          </span>
        </button>
      </div>
      {error && <div className="our-contract-number-error">{error}</div>}
    </div>
  );
}

export default OurContractNumberField;
